import React, { useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, FolderKanban, FileSignature, MapPinned, Map, Radar,
  ClipboardList, Camera, FileText, UsersRound, GraduationCap, UserCog,
  Briefcase, Receipt, BarChart3, Settings, LogOut, X, Brain, CalendarClock,
  MessagesSquare, Network, Radio, Clock, Shield, BellRing, Lock, Inbox, UserCheck,
} from 'lucide-react';
import { usePerms } from '../../context/PermissionsContext';

// Iter120L: every role EXCEPT 'client'. Used to gate modules that clients
// must never see per Phase 4/5/13 of the architecture update.
const STAFF_ROLES = [
  'main_hub', 'super_admin', 'department_admin', 'training_director',
  'field_investigator', 'skip_tracer', 'process_server', 'trainee',
];

/**
 * THERMS-style fullscreen module grid that replaces the persistent side rail.
 * Triggered by the Menu button (mobile bottom-nav + topbar). Shows every
 * accessible module as a flat navy-blue square tile with white icon + label,
 * plus a prominent "Go Off-Duty" tile at the bottom that signs the user out.
 *
 * Visibility rules (role / training-permission gates) match NxRail exactly.
 * Iter103: also honours effective module permissions from the Main Hub —
 * any module toggled OFF in /api/permissions is HIDDEN from this grid.
 */
const ADMIN_ONLY = ['main_hub', 'department_admin', 'super_admin'];

const MODULES = [
  { path: '/dashboard',             label: 'Dashboard',         icon: LayoutDashboard, moduleId: 'dashboard' },
  { path: '/cases',                 label: 'Cases',             icon: FolderKanban,   moduleId: 'cases' },
  { path: '/case-logs-hub',        label: 'Case Logs',         icon: ClipboardList,  moduleId: 'case_logs' },
  { path: '/messages',              label: 'Messages',          icon: MessagesSquare, moduleId: 'messaging' },
  { path: '/case-acceptance',       label: 'Acceptance',        icon: FileSignature,  moduleId: 'case_acceptance', roles: STAFF_ROLES },
  { path: '/investigator-tracking', label: 'GPS Tracking',      icon: MapPinned,      moduleId: 'gps_tracking',    roles: STAFF_ROLES },
  { path: '/case-operations',       label: 'Field Map',         icon: Map,            moduleId: 'operations_map',  roles: STAFF_ROLES },
  { path: '/field-map',              label: 'Field Map Hub',     icon: Map,            moduleId: 'operations_map',  roles: STAFF_ROLES },
  { path: '/skip-trace',            label: 'Skip Trace',        icon: Radar,          moduleId: 'skip_trace',      roles: STAFF_ROLES },
  { path: '/my-time-clock',          label: 'My Time Clock',     icon: Clock,          roles: STAFF_ROLES },
  { path: '/admin/timesheets',       label: 'Admin Timesheets',  icon: Clock,          roles: ADMIN_ONLY },
  { path: '/ai-investigation',      label: 'AI Investigation',  icon: Brain,         roles: STAFF_ROLES },
  { path: '/tutorials',             label: 'Tutorials',         icon: GraduationCap, moduleId: 'tutorials' },
  { path: '/nexus-new-hire',        label: 'Recruitment',       icon: UsersRound,    roles: ADMIN_ONLY },
  { path: '/training-center',       label: 'Training',          icon: GraduationCap, gate: 'training_center_access', roles: STAFF_ROLES },
  { path: '/team',                  label: 'Nexus Investigators', icon: UserCog,     roles: ['department_admin'], moduleId: 'team' },
  { path: '/clients-hub',           label: 'Clients',           icon: Briefcase,     roles: ['department_admin', 'main_hub'], moduleId: 'clients' },
  { path: '/client-documents',      label: 'Client Docs',       icon: FileSignature, moduleId: 'client_documents' },
  { path: '/admin/tips',            label: 'Public Tips',       icon: Shield,        roles: ADMIN_ONLY },
  { path: '/admin/control',         label: 'NexusTrac Control', icon: Lock,          roles: ADMIN_ONLY },
  { path: '/invoices',              label: 'Billing',           icon: Receipt,       moduleId: 'invoices' },
  { path: '/analytics',             label: 'Analytics',         icon: BarChart3,     roles: ['department_admin', 'main_hub'], moduleId: 'analytics' },
  { path: '/settings',              label: 'Settings',          icon: Settings,      moduleId: 'settings' },
];

const ADMIN_ROLES = new Set(['main_hub', 'department_admin', 'training_director', 'super_admin']);

export function NxMenuGrid({ user, onClose, onSignOut }) {
  const navigate = useNavigate();
  const { canAccessModule } = usePerms();

  // ESC closes the menu — matches native iOS/Android sheet behaviour.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Lock body scroll while the grid is open.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const visible = useMemo(() => MODULES.filter(item => {
    if (item.roles && !item.roles.includes(user?.role)) return false;
    if (item.gate === 'training_center_access') {
      if (ADMIN_ROLES.has(user?.role)) return true;
      const perms = user?.training_permissions || {};
      return !!(perms.training_center_access || user?.training_center_access);
    }
    // Iter103: also enforce Main Hub effective-permission gating.
    if (item.moduleId && !canAccessModule(item.moduleId)) return false;
    return true;
  }), [user, canAccessModule]);

  const handleOffDuty = () => {
    onClose?.();
    onSignOut?.();
  };

  return (
    <div
      className="nx-menu-grid-shell"
      data-testid="nx-menu-grid"
      role="dialog"
      aria-modal="true"
      aria-label="Navigation menu"
    >
      {/* Header — close button + page title (matches the user's reference screen) */}
      <header className="nx-menu-grid-header">
        <button
          type="button"
          onClick={onClose}
          className="nx-menu-grid-close"
          aria-label="Close menu"
          data-testid="nx-menu-close"
        >
          <X size={20} />
        </button>
        <div className="nx-menu-grid-title">
          <div className="nx-eyebrow">NEXUS // MISSION COMMAND</div>
          <h2>Modules</h2>
        </div>
        <div className="nx-menu-grid-user">
          <div className="nx-menu-grid-avatar">
            {(user?.name || 'U').charAt(0).toUpperCase()}
          </div>
          <div className="leading-tight">
            <div className="nx-menu-grid-user-name">{user?.name || 'Operator'}</div>
            <div className="nx-menu-grid-user-role">
              {(user?.role || 'operator').replace(/_/g, ' ')}
            </div>
          </div>
        </div>
      </header>

      {/* Tile grid — auto-flowing 3-col mobile / 4-col tablet / 6-col desktop */}
      <div className="nx-menu-grid-scroll">
        <div className="nx-menu-grid" data-testid="nx-menu-grid-tiles">
          {visible.map(item => {
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                className="nx-tile"
                data-testid={`nx-tile-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <Icon size={32} strokeWidth={1.6} />
                <span className="nx-tile-label">{item.label}</span>
              </Link>
            );
          })}
        </div>

        {/* Go Off-Duty — primary action, full-width, red-tinted to signal end-of-shift */}
        <button
          type="button"
          onClick={handleOffDuty}
          className="nx-offduty-tile"
          data-testid="nx-offduty-btn"
        >
          <LogOut size={20} />
          <span>Go Off-Duty</span>
          <span className="nx-offduty-tile-sub">Sign out and end session</span>
        </button>
      </div>
    </div>
  );
}

export default NxMenuGrid;
